// Manages the FullCalendar instance for the tasks page.
// Config is read from data-* attributes on #fullcalendar so this module is
// never re-executed on Turbo frame updates — listeners register once.

import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";

// Creates the FullCalendar instance from #fullcalendar data attributes.
function initCalendar() {
  const el = document.getElementById("fullcalendar");
  if (!el) return;

  if (window._phqCalendar) {
    window._phqCalendar.destroy();
    window._phqCalendar = null;
  }

  function isMobile() {
    return window.innerWidth < 768;
  }

  function toolbarFor(mobile) {
    return mobile
      ? { left: "dayGridMonth,listWeek", center: "title", right: "prev,next" }
      : {
          left: "dayGridMonth,timeGridWeek,listWeek",
          center: "title",
          right: "today prevYear,nextYear prev,next",
        };
  }

  window._phqCalendar = new Calendar(el, {
    plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
    eventSources: [
      {
        url: el.dataset.eventSourceUrl,
        startParam: el.dataset.fromParam,
        endParam: el.dataset.toParam,
        extraParams: { fullcalendar: "true" },
      },
    ],
    // Shows a simple user-facing error when the event feed cannot load.
    eventSourceFailure: function () {
      alert("Calendar loading failed, try again later");
    },

    initialView: isMobile() ? "listWeek" : "dayGridMonth",
    headerToolbar: toolbarFor(isMobile()),
    navLinks: true,
    nowIndicator: true,
    height: "auto",
    businessHours: {
      daysOfWeek: [1, 2, 3, 4, 5],
      startTime: "08:00",
      endTime: "18:00",
    },

    windowResize: function () {
      window._phqCalendar.setOption("headerToolbar", toolbarFor(isMobile()));
    },

    // Opens event links in a new tab while preserving FullCalendar navigation.
    eventClick: function (event) {
      if (event.event.url) {
        event.jsEvent.preventDefault();
        window.open(event.event.url, "_blank");
      }
    },
  });

  window._phqCalendar.render();
}

// Destroys the calendar so Turbo does not cache plugin-generated DOM.
function teardownCalendar() {
  if (window._phqCalendar) {
    window._phqCalendar.destroy();
    window._phqCalendar = null;
  }
}

// Initialize on full Turbo visits.
document.addEventListener("turbo:load", initCalendar);

// Destroy before Turbo snapshots the page so the cached DOM is clean.
document.addEventListener("turbo:before-cache", teardownCalendar);

// turbo:load doesn't fire on frame-only updates — re-init when the
// enclosing frame reloads so the calendar picks up fresh filter params.
document.addEventListener("turbo:frame-load", function (e) {
  const el = document.getElementById("fullcalendar");
  if (!el || !el.dataset.frameId) return;
  if (e.target.id === el.dataset.frameId) initCalendar();
});
